import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input, FormField, ToggleGroup } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import api from '../../services/api';

const STEPS = { EMAIL: 'email', OTP: 'otp', PASSWORD: 'password', DONE: 'done' };

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const otpRefs = useRef([]);

  function handleOtpChange(index, value) {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  }

  function handleOtpKeyDown(index, e) {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  }

  function handleOtpPaste(e) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
    }
    e.preventDefault();
  }

  async function handleEmailSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setStep(STEPS.OTP);
    } catch (err) {
      const msg = err?.response?.data?.message;
      setError(typeof msg === 'string' ? msg : t('common.error_occurred'));
    } finally {
      setLoading(false);
    }
  }

  async function handleOtpSubmit(e) {
    e.preventDefault();
    setError('');
    const code = otp.join('');
    if (code.length < 6) {
      setError(t('forgot_password.otp_incomplete'));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/verify-otp', { email, otp: code });
      setResetToken(data.resetToken);
      setStep(STEPS.PASSWORD);
    } catch (err) {
      const msg = err?.response?.data?.message;
      setError(typeof msg === 'string' ? msg : t('common.error_occurred'));
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordSubmit(e) {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError(t('forgot_password.passwords_mismatch'));
      return;
    }
    if (newPassword.length < 8) {
      setError(t('forgot_password.password_min'));
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { resetToken, newPassword });
      setStep(STEPS.DONE);
    } catch (err) {
      const msg = err?.response?.data?.message;
      setError(typeof msg === 'string' ? msg : t('common.error_occurred'));
    } finally {
      setLoading(false);
    }
  }

  const brandPanel = (
    <div className="hidden lg:flex lg:w-1/2 bg-primary flex-col justify-between p-10 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-100px] left-[-100px] w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-[-80px] right-[-80px] w-80 h-80 rounded-full bg-accent/20 blur-3xl" />
      </div>
      <div className="relative flex items-center gap-3">
        <div className="w-16 h-15 rounded-lg bg-white flex items-center justify-center overflow-hidden">
          <img
            src="/mfb-logo.png"
            alt="MFB"
            className="w-20 h-12 object-contain mb-1"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement.innerHTML =
                '<span class="text-accent font-black text-sm">MFB</span>';
            }}
          />
        </div>
        <div>
          <p className="text-white font-bold text-[15px] leading-tight">{t('brand.name')}</p>
          <p className="text-white/50 text-[11px]">{t('brand.tagline')}</p>
        </div>
      </div>
      <div className="relative">
        <h1 className="text-4xl font-bold leading-tight text-white mb-4">
          {t('forgot_password.panel_headline')}
        </h1>
        <p className="text-white/60 text-[13px] leading-relaxed max-w-sm">
          {t('forgot_password.panel_subheadline')}
        </p>
      </div>
      <p className="relative text-white/30 text-[11px]">{t('brand.footer')}</p>
    </div>
  );

  return (
    <div className="min-h-screen flex">
      {brandPanel}

      <div className="flex-1 flex flex-col bg-white">
        <div className="flex justify-end p-6">
          <ToggleGroup
            options={[{ value: 'fr', label: 'FR' }, { value: 'en', label: 'EN' }]}
            value={i18n.language === 'en' ? 'en' : 'fr'}
            onChange={(lang) => i18n.changeLanguage(lang)}
          />
        </div>

        <div className="flex-1 flex items-center justify-center px-8">
          <div className="w-full max-w-sm">

            {/* Step indicator */}
            {step !== STEPS.DONE && (
              <div className="flex items-center gap-2 mb-8">
                {[STEPS.EMAIL, STEPS.OTP, STEPS.PASSWORD].map((s, i) => {
                  const stepIndex = [STEPS.EMAIL, STEPS.OTP, STEPS.PASSWORD].indexOf(step);
                  const done = i < stepIndex;
                  const active = i === stepIndex;
                  return (
                    <React.Fragment key={s}>
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors ${
                          done ? 'bg-primary text-white' : active ? 'bg-primary text-white ring-2 ring-primary/20' : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        {done ? <CheckCircle size={12} /> : i + 1}
                      </div>
                      {i < 2 && (
                        <div className={`flex-1 h-px transition-colors ${done ? 'bg-primary' : 'bg-slate-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {/* ── Step 1: Email ── */}
            {step === STEPS.EMAIL && (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-900">{t('forgot_password.email_title')}</h2>
                  <p className="text-[13px] text-slate-500 mt-1">{t('forgot_password.email_subtitle')}</p>
                </div>
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <FormField label={t('login.email_label')} required>
                    <Input
                      type="email"
                      placeholder={t('login.email_placeholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                  </FormField>
                  {error && (
                    <p className="text-[12px] text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">{error}</p>
                  )}
                  <Button type="submit" variant="primary" size="lg" loading={loading} iconRight={!loading ? ArrowRight : undefined} className="w-full mt-2">
                    {t('forgot_password.email_submit')}
                  </Button>
                </form>
                <div className="mt-6 text-center">
                  <Link to="/login" className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-primary transition">
                    <ArrowLeft size={12} />{t('signup.back_to_login')}
                  </Link>
                </div>
              </>
            )}

            {/* ── Step 2: OTP ── */}
            {step === STEPS.OTP && (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-900">{t('forgot_password.otp_title')}</h2>
                  <p className="text-[13px] text-slate-500 mt-1">
                    {t('forgot_password.otp_subtitle')} <span className="font-semibold text-slate-700">{email}</span>
                  </p>
                </div>
                <form onSubmit={handleOtpSubmit} className="space-y-6">
                  <div>
                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-3 block">
                      {t('forgot_password.otp_label')} <span className="text-rose-500">*</span>
                    </label>
                    <div className="flex gap-2.5 justify-center" onPaste={handleOtpPaste}>
                      {otp.map((digit, i) => (
                        <input
                          key={i}
                          ref={(el) => (otpRefs.current[i] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(i, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(i, e)}
                          className="w-11 h-12 text-center text-lg font-bold border-2 rounded-lg outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-slate-900 border-slate-200"
                        />
                      ))}
                    </div>
                  </div>
                  {error && (
                    <p className="text-[12px] text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">{error}</p>
                  )}
                  <Button type="submit" variant="primary" size="lg" loading={loading} iconRight={!loading ? ArrowRight : undefined} className="w-full">
                    {t('forgot_password.otp_submit')}
                  </Button>
                </form>
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => { setStep(STEPS.EMAIL); setOtp(['', '', '', '', '', '']); setError(''); }}
                    className="text-[12px] text-slate-500 hover:text-primary transition"
                  >
                    {t('forgot_password.otp_resend')}
                  </button>
                </div>
              </>
            )}

            {/* ── Step 3: New password ── */}
            {step === STEPS.PASSWORD && (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-900">{t('forgot_password.password_title')}</h2>
                  <p className="text-[13px] text-slate-500 mt-1">{t('forgot_password.password_subtitle')}</p>
                </div>
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <FormField label={t('forgot_password.new_password_label')} required>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff size={14} strokeWidth={1.75} /> : <Eye size={14} strokeWidth={1.75} />}
                      </button>
                    </div>
                  </FormField>
                  <FormField label={t('forgot_password.confirm_password_label')} required>
                    <div className="relative">
                      <Input
                        type={showConfirm ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm((v) => !v)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                        tabIndex={-1}
                      >
                        {showConfirm ? <EyeOff size={14} strokeWidth={1.75} /> : <Eye size={14} strokeWidth={1.75} />}
                      </button>
                    </div>
                  </FormField>
                  {error && (
                    <p className="text-[12px] text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">{error}</p>
                  )}
                  <Button type="submit" variant="primary" size="lg" loading={loading} iconRight={!loading ? ArrowRight : undefined} className="w-full mt-2">
                    {t('forgot_password.password_submit')}
                  </Button>
                </form>
              </>
            )}

            {/* ── Done ── */}
            {step === STEPS.DONE && (
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <CheckCircle size={32} className="text-emerald-600" strokeWidth={1.5} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('forgot_password.done_title')}</h2>
                <p className="text-[13px] text-slate-500 mb-8">{t('forgot_password.done_message')}</p>
                <Button variant="primary" size="lg" className="w-full" onClick={() => navigate('/login', { replace: true })}>
                  {t('signup.signin_link')}
                </Button>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
